import differenceWith from "lodash/differenceWith";
import take from "lodash/take";
import { graphql, readInlineData } from "react-relay";

import type {
  helpersStoryStyleArticleListQuery$data,
  helpersStoryStyleArticleListQuery$key,
} from "~/queries/__generated__/helpersStoryStyleArticleListQuery.graphql";

import type { SubSection } from "./consts";
import { MaximumArticleNumberPerSection, SectionNameMap } from "./consts";
import type { Section } from "./types";

export type UniqueArticlesPerSectionItem = {
  articles: ArticleList;
  section: Section;
};

// Remember to check the queue in Drupal CMS and click the save to sync the articles(For staging).
export function getUniqueArticlesPerSection(parameters: GetDeDuplicatedArticleListParameters) {
  const { currentSectionEntityId, reference } = parameters;

  const data = readInlineData(query, reference);

  const subSectionToArticleListPairList: [SubSection, ArticleList][] = [
    ["luxury", getArticleList({ queue: data.luxury })],
    ["fashion", getArticleList({ queue: data.fashion })],
    ["beauty", getArticleList({ queue: data.beauty })],
    ["people", getArticleList({ queue: data.people })],
    ["lifestyle", getArticleList({ queue: data.lifestyle })],
  ];

  const uniqueArticlesPerSection = subSectionToArticleListPairList.reduce(
    (result: UniqueArticlesPerSectionItem[], [sectionNameKey, currentArticleList]) => {
      const filteredArticleList = take(
        differenceWith(
          currentArticleList,
          result.flatMap(({ articles }) => articles),
          (currentArticle, selectedArticle) => currentArticle.entityId === selectedArticle.entityId,
        ),
        MaximumArticleNumberPerSection,
      );

      return [
        ...result,
        {
          articles: filteredArticleList,
          section: SectionNameMap[sectionNameKey],
        },
      ];
    },
    [],
  );

  const matchedSectionIndex = uniqueArticlesPerSection.findIndex(
    ({ section }) => section.entityId === currentSectionEntityId,
  );

  if (matchedSectionIndex === -1) {
    // If the current section is not found, return the default ordering, e.g. /magazines/style.
    return uniqueArticlesPerSection;
  }

  return [
    ...uniqueArticlesPerSection.slice(matchedSectionIndex),
    ...uniqueArticlesPerSection.slice(0, matchedSectionIndex),
  ];
}

type GetArticleListParameters = {
  queue: helpersStoryStyleArticleListQuery$data[SubSection];
};

export type ArticleList = ReturnType<typeof getArticleList>;

type GetDeDuplicatedArticleListParameters = {
  currentSectionEntityId: string;
  reference: helpersStoryStyleArticleListQuery$key;
};

function getArticleList(parameters: GetArticleListParameters) {
  const { queue } = parameters;
  const edgeList = queue?.items?.edges ?? [];
  return edgeList.map(edge => edge.node) ?? [];
}

const query = graphql`
  fragment helpersStoryStyleArticleListQuery on Query @inline {
    luxury: queue(filter: { entityId: "1070" }) {
      items(first: 4, exclude: { types: VIDEO })
        @connection(key: "helpersStoryStyleArticleListQuery__items") {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }

    fashion: queue(filter: { name: "section_top_516294" }) {
      items(first: 8, exclude: { types: VIDEO })
        @connection(key: "helpersStoryStyleArticleListQuery__items") {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    beauty: queue(filter: { name: "section_top_516298" }) {
      items(first: 8, exclude: { types: VIDEO })
        @connection(key: "helpersStoryStyleArticleListQuery__items") {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    people: queue(filter: { name: "section_top_516301" }) {
      items(first: 8, exclude: { types: VIDEO })
        @connection(key: "helpersStoryStyleArticleListQuery__items") {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    lifestyle: queue(filter: { name: "section_top_516305" }) {
      items(first: 8, exclude: { types: VIDEO })
        @connection(key: "helpersStoryStyleArticleListQuery__items") {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
  }
`;
